import jsPDF from 'jspdf';
import 'jspdf-autotable'; 

/**
 * Export table data to PDF with proper formatting, pagination, and styling
 * @param {Array} data - The filtered data to export
 * @param {Array} columns - Column configuration with id, name, labelFormat, etc.
 * @param {string} fileName - The name for the PDF file
 * @param {string} tableTitle - Title to display at the top of the PDF
 */
export const exportToPdf = (data, columns, fileName, tableTitle = 'Table Export') => {
    try {
        // Validate inputs
        if (!data || !Array.isArray(data)) {
            throw new Error('Data must be an array');
        }
        if (!columns || !Array.isArray(columns)) {
            throw new Error('Columns must be an array');
        }
        
        // Create new PDF document
        const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation for better table display
        
        // Verify autoTable plugin is available
        if (typeof doc.autoTable !== 'function') {
            throw new Error('jsPDF-AutoTable plugin is not loaded properly. Make sure jspdf-autotable is installed and imported correctly.');
        }
        
        // Add title
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text(tableTitle, 20, 20);
        
        // Add export date
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const exportDate = `Exported on: ${new Date().toLocaleString()}`;
        doc.text(exportDate, 20, 30);
        
        // Filter out non-exportable columns (similar to Excel export logic)
        const filteredColumns = columns.filter(
            col => !['editItem', 'deleteItem', 'clickToItem', 'clickToObject'].includes(col.labelFormat)
        );
        
        // Prepare table headers
        const headers = filteredColumns.map(col => col.name || col.id);
        
        // Prepare table data
        const tableData = data.map(item => {
            return filteredColumns.map(col => {
                let value = item[col.id];
                
                // Format values based on column format
                if (col.labelFormat === 'dollar') {
                    const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
                    return new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD'
                    }).format(numValue);
                } else if (col.labelFormat === 'date') {
                    return value ? new Date(value).toLocaleDateString() : '';
                } else if (col.labelFormat === 'percentagePill') {
                    return `${value ?? 0}%`;
                } else if (col.labelFormat === 'linkToItem' || col.labelFormat === 'linkToObject') {
                    // For links, just show the text content
                    return value ?? '';
                }
                
                return value ?? '';
            });
        });
        
        // Calculate column widths based on content
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 20;
        const availableWidth = pageWidth - (margin * 2);
        const columnCount = headers.length;
        
        // Base column width
        let columnWidths = new Array(columnCount).fill(availableWidth / columnCount);
        
        // Adjust widths for specific column types
        filteredColumns.forEach((col, index) => {
            if (col.labelFormat === 'dollar') {
                columnWidths[index] = Math.min(30, availableWidth / columnCount * 1.2);
            } else if (col.id === 'id' || col.id === 'priority') {
                columnWidths[index] = Math.min(20, availableWidth / columnCount * 0.8);
            }
        });
        
        // Normalize column widths to fit available space
        const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);
        if (totalWidth > availableWidth) {
            const scale = availableWidth / totalWidth;
            columnWidths = columnWidths.map(width => width * scale);
        }
        
        // Configure autoTable options
        const tableOptions = {
            head: [headers],
            body: tableData,
            startY: 40,
            margin: { left: margin, right: margin },
            styles: {
                fontSize: 8,
                cellPadding: 3,
                overflow: 'linebreak',
                halign: 'left'
            },
            headStyles: {
                fillColor: [51, 122, 183], // Bootstrap primary blue
                textColor: 255,
                fontStyle: 'bold',
                fontSize: 9
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245] // Light gray for alternate rows
            },
            tableWidth: 'auto',
            columnStyles: {}
        };
        
        // Set column-specific styles
        filteredColumns.forEach((col, index) => {
            const colStyle = {};
            
            if (col.labelFormat === 'dollar') {
                colStyle.halign = 'right';
            } else if (col.id === 'id' || col.id === 'priority') {
                colStyle.halign = 'center';
            }
            
            // Set column width
            colStyle.cellWidth = columnWidths[index];
            
            tableOptions.columnStyles[index] = colStyle;
        });
        
        // Generate the table
        doc.autoTable(tableOptions);
        
        // Add footer with page numbers
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setFont(undefined, 'normal');
            const footerText = `Page ${i} of ${pageCount}`;
            const footerWidth = doc.getStringUnitWidth(footerText) * 8 / doc.internal.scaleFactor;
            doc.text(footerText, pageWidth - footerWidth - margin, doc.internal.pageSize.getHeight() - 10);
        }
        
        // Add metadata
        doc.setProperties({
            title: tableTitle,
            subject: `${tableTitle} - Data Export`,
            author: 'Renu360',
            creator: 'Renu360 Application',
            producer: 'jsPDF'
        });
        
        // Generate filename with date if not provided
        const finalFileName = fileName || `${tableTitle.replace(/[^a-zA-Z0-9]/g, '_')}_Export_${new Date().toISOString().split('T')[0]}`;
        
        // Save the PDF
        doc.save(`${finalFileName}.pdf`);
        
        return true;
        
    } catch (error) {
        console.error('Error generating PDF:', error);
        console.error('Error details:', {
            message: error.message,
            stack: error.stack,
            data: data?.length,
            columns: columns?.length
        });
        alert(`An error occurred while generating the PDF: ${error.message}. Please check the console for details.`);
        return false;
    }
};

/**
 * Preview table data in a new window before exporting to PDF
 * @param {Array} data - The filtered data to preview
 * @param {Array} columns - Column configuration
 * @param {string} tableTitle - Title for the preview
 */
export const previewTableAsPdf = (data, columns, tableTitle = 'Table Preview') => {
    // Create a preview window with table data
    const previewWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes');
    
    const filteredColumns = columns.filter(
        col => !['editItem', 'deleteItem', 'clickToItem', 'clickToObject'].includes(col.labelFormat)
    );
    
    let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>${tableTitle} - Preview</title>
            <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                h1 { color: #337ab7; margin-bottom: 10px; }
                .export-info { color: #666; margin-bottom: 20px; font-size: 14px; }
                table { border-collapse: collapse; width: 100%; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #337ab7; color: white; font-weight: bold; }
                tr:nth-child(even) { background-color: #f5f5f5; }
                .currency { text-align: right; }
                .center { text-align: center; }
                .print-btn { 
                    margin-bottom: 20px; 
                    padding: 10px 20px; 
                    background-color: #337ab7; 
                    color: white; 
                    border: none; 
                    cursor: pointer; 
                    font-size: 14px;
                }
                .print-btn:hover { background-color: #286090; }
            </style>
        </head>
        <body>
            <button class="print-btn" onclick="window.print()">Print Preview</button>
            <h1>${tableTitle}</h1>
            <div class="export-info">Exported on: ${new Date().toLocaleString()}</div>
            <table>
                <thead>
                    <tr>
                        ${filteredColumns.map(col => `<th>${col.name || col.id}</th>`).join('')}
                    </tr>
                </thead>
                <tbody>
    `;
    
    data.forEach(item => {
        htmlContent += '<tr>';
        filteredColumns.forEach(col => {
            let value = item[col.id];
            let cssClass = '';
            
            if (col.labelFormat === 'dollar') {
                const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
                value = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                }).format(numValue);
                cssClass = 'currency';
            } else if (col.labelFormat === 'date') {
                value = value ? new Date(value).toLocaleDateString() : '';
            } else if (col.labelFormat === 'percentagePill') {
                value = `${value ?? 0}%`;
                cssClass = 'center';
            } else if (col.id === 'id' || col.id === 'priority') {
                cssClass = 'center';
            }
            
            htmlContent += `<td class="${cssClass}">${value ?? ''}</td>`;
        });
        htmlContent += '</tr>';
    });
    
    htmlContent += `
                </tbody>
            </table>
        </body>
        </html>
    `;
    
    previewWindow.document.write(htmlContent);
    previewWindow.document.close();
};

const pdfUtils = { exportToPdf, previewTableAsPdf };
export default pdfUtils;